import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, ArrowLeft, Check, Trash2, X } from 'lucide-react';
import { GsAccessLevel, GsGrantReviewStatus, GsResourceType } from '@/lib/api';
import {
  useAdminGrant,
  useApproveGrant,
  useDeleteGrant,
  useRejectGrant,
  useUpsertGrant,
} from '../hooks';

function statusBadgeVariant(
  status: GsGrantReviewStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'approved') return 'default';
  if (status === 'rejected') return 'destructive';
  return 'secondary';
}

export function AdminGrantDetailPage() {
  const { archeId, resourceType, resourceName } = useParams<{
    archeId: string;
    resourceType: GsResourceType;
    resourceName: string;
  }>();
  const navigate = useNavigate();
  const { grant, loading, error, refresh } = useAdminGrant(
    archeId || '',
    (resourceType as GsResourceType) || 'state',
    resourceName || '',
  );

  const approveMutation = useApproveGrant();
  const rejectMutation = useRejectGrant();
  const upsertMutation = useUpsertGrant();
  const deleteMutation = useDeleteGrant();

  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showAccessChange, setShowAccessChange] = useState(false);
  const [pendingAccess, setPendingAccess] = useState<GsAccessLevel>('read');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const backPath = '/a/arche_dfe_game_state/admin/grants';

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (error || !grant) {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(backPath)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <Card className="border-destructive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              {error ? 'Error' : 'Not Found'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error || 'Grant not found.'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleApprove = () => {
    approveMutation.mutate(
      {
        archeId: grant.archeId,
        resourceType: grant.resourceType,
        resourceName: grant.resourceName,
      },
      { onSuccess: () => refresh() },
    );
  };

  const handleReject = () => {
    rejectMutation.mutate(
      {
        archeId: grant.archeId,
        resourceType: grant.resourceType,
        resourceName: grant.resourceName,
        reason: rejectReason.trim() || undefined,
      },
      {
        onSuccess: () => {
          setShowRejectForm(false);
          setRejectReason('');
          refresh();
        },
      },
    );
  };

  const handleAccessChange = () => {
    upsertMutation.mutate(
      {
        archeId: grant.archeId,
        resourceType: grant.resourceType,
        resourceName: grant.resourceName,
        access: pendingAccess,
      },
      {
        onSuccess: () => {
          setShowAccessChange(false);
          refresh();
        },
      },
    );
  };

  const handleDelete = () => {
    deleteMutation.mutate(
      {
        archeId: grant.archeId,
        resourceType: grant.resourceType,
        resourceName: grant.resourceName,
      },
      { onSuccess: () => navigate(backPath) },
    );
  };

  const isCapIncreasing = grant.access === 'read' && pendingAccess === 'read_write';

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to={backPath}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Grants
        </Link>
        <h1 className="text-2xl font-semibold">Plugin Grant</h1>
        <p className="text-sm text-muted-foreground mt-1 font-mono">
          {grant.archeId} / {grant.resourceType} / {grant.resourceName}
        </p>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <Badge variant={statusBadgeVariant(grant.reviewStatus)}>{grant.reviewStatus}</Badge>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Access</p>
              <p className="font-mono">{grant.access}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Created by</p>
              <p className="font-mono text-xs truncate">{grant.createdBy}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Created at</p>
              <p>{new Date(grant.createdAt).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Reviewed by</p>
              <p className="font-mono text-xs truncate">{grant.reviewedBy ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Reviewed at</p>
              <p>{grant.reviewedAt ? new Date(grant.reviewedAt).toLocaleString() : '—'}</p>
            </div>
          </div>
          {grant.reviewReason && (
            <div>
              <p className="text-xs text-muted-foreground">Reject reason</p>
              <p className="text-sm">{grant.reviewReason}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {grant.reviewStatus !== 'approved' && (
              <Button onClick={handleApprove} disabled={approveMutation.isPending}>
                <Check className="h-4 w-4 mr-1" />
                {approveMutation.isPending ? 'Approving...' : 'Approve'}
              </Button>
            )}
            {grant.reviewStatus !== 'rejected' && (
              <Button
                variant="outline"
                onClick={() => setShowRejectForm((s) => !s)}
                disabled={rejectMutation.isPending}
              >
                <X className="h-4 w-4 mr-1" />
                Reject
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowAccessChange((s) => !s)}>
              Change access
            </Button>
            {confirmDelete ? (
              <>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                >
                  Confirm delete
                </Button>
                <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            )}
          </div>

          {showRejectForm && (
            <div className="space-y-2 pt-2 border-t">
              <label className="text-xs text-muted-foreground">
                Reason (optional, max 1000 chars)
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value.slice(0, 1000))}
                rows={3}
                className="w-full px-3 py-2 border rounded-md bg-background text-sm"
              />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setShowRejectForm(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleReject}
                  disabled={rejectMutation.isPending}
                >
                  {rejectMutation.isPending ? 'Rejecting...' : 'Confirm reject'}
                </Button>
              </div>
            </div>
          )}

          {showAccessChange && (
            <div className="space-y-2 pt-2 border-t">
              <label className="text-xs text-muted-foreground">New access level</label>
              <select
                value={pendingAccess}
                onChange={(e) => setPendingAccess(e.target.value as GsAccessLevel)}
                className="px-3 py-2 border rounded-md bg-background text-sm"
              >
                <option value="read">read</option>
                <option value="read_write">read_write</option>
              </select>
              {isCapIncreasing && (
                <p className="text-xs text-yellow-600 dark:text-yellow-400">
                  Promoting read → read_write resets review status to <strong>pending</strong>; the
                  grant will need a fresh approval.
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setShowAccessChange(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleAccessChange}
                  disabled={upsertMutation.isPending || pendingAccess === grant.access}
                >
                  {upsertMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
